import { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, formatEur, statusBadge } from '../../utils/operations';

export const AdminReviews = ({ reviews, onRefresh }) => {
  const moderate = async (review, status) => {
    try {
      await apiFetch(`/reviews/${review.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) });
      toast.success(`Review ${status}`);
      await onRefresh();
    } catch (err) {
      toast.error('Review não atualizada', { description: String(err.message) });
    }
  };
  return (
    <div className="space-y-4" data-testid="admin-review-list">
      {reviews.map((r) => (
        <article key={r.id} data-testid={`admin-review-${r.id}`} className="rounded-xl border border-eco-ink/10 bg-white p-5">
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-eco-ink">{r.traveler_name} · {r.experience || r.package_name}</p>
              <p className="mt-1 text-xs text-eco-ink/50">{r.voucher_code} · {r.destination}{r.partner_name && ` · ${r.partner_name}`}</p>
            </div>
            <span className={`rounded-full px-2 py-1 text-[10px] font-bold ${r.verified_experience ? 'bg-eco-green/10 text-eco-green' : 'bg-amber-100 text-amber-800'}`}>{r.verified_experience ? 'Verified Experience' : 'Verification unavailable'}</span>
            <span className="flex items-center gap-1 text-eco-gold">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={14} fill="currentColor" />)}</span>
            <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${statusBadge(r.status)}`}>{r.status}</span>
          </div>
          {r.text && <p className="mt-3 text-sm leading-relaxed text-eco-ink/65">“{r.text}”</p>}
          {r.status === 'published' && <button onClick={() => moderate(r, 'rejected')} className="mt-4 inline-flex min-h-10 items-center rounded-lg border border-red-200 px-4 text-xs font-semibold text-red-600">Hide review</button>}
          {r.status === 'pending' && (
            <div className="mt-4 flex gap-2">
              <button data-testid={`review-publish-${r.id}`} onClick={() => moderate(r, 'published')} className="inline-flex min-h-10 items-center rounded-lg bg-eco-green px-4 text-xs font-semibold text-white transition-colors hover:bg-eco-deep">Publicar</button>
              <button data-testid={`review-reject-${r.id}`} onClick={() => moderate(r, 'rejected')} className="inline-flex min-h-10 items-center rounded-lg border border-red-200 px-4 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50">Rejeitar</button>
            </div>
          )}
        </article>
      ))}
      {reviews.length === 0 && <p className="rounded-xl border border-dashed border-eco-ink/15 bg-white py-12 text-center text-sm text-eco-ink/50">Sem reviews reais ainda.</p>}
    </div>
  );
};

export const AdminAnalytics = ({ analytics }) => {
  const [s22, setS22] = useState(null);
  useEffect(() => {
    apiFetch('/admin/stay22/stats').then(setS22).catch(() => setS22(null));
  }, []);
  if (!analytics) return null;
  const cards = [
    ['Pedidos', analytics.requests.total], ['Parceiros publicados', analytics.partners.published],
    ['Produtos', analytics.products.total], ['Propostas aceites', analytics.proposals.accepted],
    ['Pagamentos recebidos', formatEur(analytics.payments.paid_eur)], ['Vouchers', analytics.vouchers.total],
    ['Payouts pendentes', formatEur(analytics.payouts.pending_partner_eur)], ['Reviews publicadas', analytics.reviews.published],
  ];
  return (
    <div data-testid="admin-analytics-panel">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map(([label, value]) => (
          <div key={label} className="eco-admin-card p-5">
            <p className="text-[13px] font-medium text-eco-ink/55">{label}</p>
            <p className="eco-number mt-2 text-[28px] leading-tight font-semibold tracking-[-0.02em] text-eco-green">{value}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-eco-ink/10 bg-white p-6">
          <h2 className="text-lg leading-[1.3] font-semibold tracking-[-0.015em] text-eco-ink">Pipeline de pedidos</h2>
          <div className="mt-4 space-y-2">
            {Object.entries(analytics.requests.by_status).filter(([, n]) => n > 0).map(([status, n]) => <p key={status} className="flex justify-between text-sm text-eco-ink/65"><span>{status}</span><strong>{n}</strong></p>)}
          </div>
        </div>
        <div className="rounded-xl border border-eco-ink/10 bg-white p-6">
          <h2 className="text-lg leading-[1.3] font-semibold tracking-[-0.015em] text-eco-ink">Notificações</h2>
          <p className="mt-4 text-sm text-eco-ink/65">Enviadas: <strong>{analytics.notifications.sent}</strong></p>
          <p className="mt-2 text-sm text-eco-ink/65">Falhadas: <strong>{analytics.notifications.failed}</strong></p>
          <p className="mt-2 text-sm text-eco-ink/65">Ignoradas por configuração: <strong>{analytics.notifications.skipped}</strong></p>
          <p className="mt-4 text-xs text-eco-ink/40">Analytics calculado apenas de registos reais na base de dados.</p>
        </div>
        <div className="rounded-xl border border-eco-ink/10 bg-white p-6" data-testid="analytics-stay22">
          <h2 className="text-lg leading-[1.3] font-semibold tracking-[-0.015em] text-eco-ink">Alojamento externo (Stay22)</h2>
          {s22 ? (
            <>
              <p className="mt-4 text-sm text-eco-ink/65">Integração: <strong className={s22.integration.enabled && s22.integration.configured ? 'text-eco-green' : 'text-amber-700'}>{s22.integration.enabled && s22.integration.configured ? (s22.integration.attributed ? 'Ativa' : 'Ativa (sem atribuição)') : 'Desligada'}</strong></p>
              <p className="mt-2 text-sm text-eco-ink/65">Pesquisas iniciadas: <strong>{s22.by_surface.search_started || 0}</strong></p>
              <p className="mt-2 text-sm text-eco-ink/65">Cliques de saída para Stay22: <strong>{s22.by_surface.outbound_click || 0}</strong></p>
              <p className="mt-2 text-sm text-eco-ink/65">Alojamentos guardados para a viagem: <strong>{s22.by_surface.save_stay || 0}</strong></p>
              {Object.keys(s22.by_destination).length > 0 && (
                <div className="mt-3 space-y-1 border-t border-eco-ink/10 pt-3">
                  {Object.entries(s22.by_destination).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([dest, n]) => (
                    <p key={dest} className="flex justify-between text-xs text-eco-ink/55"><span>{dest}</span><strong>{n}</strong></p>
                  ))}
                </div>
              )}
              <p className="mt-4 text-xs text-eco-ink/40">Conversões/comissões: verificar no Stay22 Hub — não inventamos dados de conversão.</p>
            </>
          ) : (
            <p className="mt-4 text-sm text-eco-ink/50">Sem dados ainda.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export const AdminNotifications = ({ notifications }) => (
  <div className="space-y-3" data-testid="admin-notification-list">
    {notifications.map((n) => (
      <div key={n.id} data-testid={`notification-${n.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-eco-ink/10 bg-white px-5 py-4">
        <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${statusBadge(n.status)}`}>{n.status}</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-eco-ink">{n.channel} · {n.template}</p>
          <p className="text-xs text-eco-ink/50">{n.recipient || 'sem destinatário'} · {n.detail}</p>
        </div>
        <span className="text-xs text-eco-ink/40">{new Date(n.created_at).toLocaleString('pt-PT')}</span>
      </div>
    ))}
    {notifications.length === 0 && <p className="rounded-xl border border-dashed border-eco-ink/15 bg-white py-12 text-center text-sm text-eco-ink/50">Sem eventos de notificação.</p>}
  </div>
);
